import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';

const BarChart = ({ title, data, valueKey, labelKey, color = '#0f766e' }) => {
  if (!data?.length) {
    return (
      <div className="chart-card">
        <h4>{title}</h4>
        <p className="chart-empty">No data yet</p>
      </div>
    );
  }
  const max = Math.max(...data.map((d) => Number(d[valueKey] || 0)), 1);
  return (
    <div className="chart-card">
      <h4>{title}</h4>
      <div className="bar-chart">
        {data.map((d, i) => {
          const val = Number(d[valueKey] || 0);
          const pct = Math.round((val / max) * 100);
          return (
            <div key={i} className="bar-row">
              <span className="bar-label">{String(d[labelKey] ?? '').slice(0, 16)}</span>
              <div className="bar-track">
                <div className="bar-fill" style={{ width: `${pct}%`, background: color }} />
              </div>
              <span className="bar-value">{val}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DualBarChart = ({ title, data }) => {
  if (!data?.length) {
    return (
      <div className="chart-card">
        <h4>{title}</h4>
        <p className="chart-empty">No login data yet</p>
      </div>
    );
  }
  const max = Math.max(
    ...data.map((d) => Math.max(Number(d.success_count || 0), Number(d.fail_count || 0))),
    1
  );
  return (
    <div className="chart-card">
      <h4>{title}</h4>
      <div className="bar-chart">
        {data.map((d, i) => (
          <div key={i} className="bar-row dual">
            <span className="bar-label">{String(d.day).slice(5)}</span>
            <div className="bar-track stacked">
              <div
                className="bar-fill"
                style={{
                  width: `${Math.round((Number(d.success_count || 0) / max) * 100)}%`,
                  background: '#0f766e',
                }}
                title={`Success: ${d.success_count}`}
              />
              <div
                className="bar-fill"
                style={{
                  width: `${Math.round((Number(d.fail_count || 0) / max) * 100)}%`,
                  background: '#b45309',
                }}
                title={`Failed: ${d.fail_count}`}
              />
            </div>
            <span className="bar-value">
              {d.success_count || 0}/{d.fail_count || 0}
            </span>
          </div>
        ))}
      </div>
      <p className="chart-legend">
        <span className="leg success">Success</span>
        <span className="leg fail">Failed</span>
      </p>
    </div>
  );
};

const SystemAdminPanel = ({ showNotification }) => {
  const [health, setHealth] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [memberAccounts, setMemberAccounts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loginAttempts, setLoginAttempts] = useState([]);
  const [tab, setTab] = useState('overview');
  const [userForm, setUserForm] = useState({ email: '', password: '', role: 'marketing' });
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [h, a, u, ma, l, la] = await Promise.allSettled([
        api.get('/system/health'),
        api.get('/system/analytics?days=14'),
        api.get('/system/users'),
        api.get('/system/member-accounts'),
        api.get('/system/logs?limit=80'),
        api.get('/system/login-attempts?limit=80'),
      ]);
      if (h.status === 'fulfilled') setHealth(h.value.data);
      if (a.status === 'fulfilled') setAnalytics(a.value.data);
      if (u.status === 'fulfilled') setUsers(Array.isArray(u.value.data) ? u.value.data : []);
      if (ma.status === 'fulfilled') setMemberAccounts(Array.isArray(ma.value.data) ? ma.value.data : []);
      if (l.status === 'fulfilled') setLogs(Array.isArray(l.value.data) ? l.value.data : []);
      if (la.status === 'fulfilled') setLoginAttempts(Array.isArray(la.value.data) ? la.value.data : []);
    } catch (e) {
      showNotification?.('Failed to load system admin data', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/system/users', userForm);
      showNotification?.('User created', 'success');
      setUserForm({ email: '', password: '', role: 'marketing' });
      await loadAll();
    } catch (err) {
      showNotification?.(err.response?.data?.message || 'Failed to create user', 'error');
    }
  };

  const updateUserRole = async (id, role) => {
    try {
      await api.put(`/system/users/${id}`, { role });
      showNotification?.('Role updated', 'success');
      await loadAll();
    } catch (err) {
      showNotification?.(err.response?.data?.message || 'Update failed', 'error');
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await api.delete(`/system/users/${u.id}`);
      showNotification?.('User deleted', 'success');
      await loadAll();
    } catch (err) {
      showNotification?.(err.response?.data?.message || 'Delete failed', 'error');
    }
  };

  const deleteMemberAccount = async (acc) => {
    if (!window.confirm(`Delete portal account ${acc.employment_number}?`)) return;
    try {
      await api.delete(`/system/member-accounts/${acc.id}`);
      showNotification?.('Portal account deleted', 'success');
      await loadAll();
    } catch (err) {
      showNotification?.(err.response?.data?.message || 'Delete failed', 'error');
    }
  };

  if (loading && !health) {
    return <div className="loading-state"><div className="loading-spinner" /><p>Loading system admin…</p></div>;
  }

  return (
    <div className="system-admin-panel">
      <div className="system-tabs">
        {[
          { key: 'overview', label: 'Health & Charts' },
          { key: 'users', label: 'Users' },
          { key: 'accounts', label: 'Portal Accounts' },
          { key: 'logs', label: 'Activity Logs' },
          { key: 'logins', label: 'Login Attempts' },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            className={`btn ${tab === t.key ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
        <button type="button" className="btn btn-secondary" onClick={loadAll}>Refresh</button>
      </div>

      {tab === 'overview' && (
        <>
          {health && (
            <div className="system-health-grid">
              <div className="health-card">
                <span className="health-label">Uptime</span>
                <strong>{Math.floor((health.uptimeSeconds || 0) / 60)} min</strong>
              </div>
              <div className="health-card">
                <span className="health-label">Memory</span>
                <strong>{health.memoryMb} MB</strong>
              </div>
              <div className="health-card">
                <span className="health-label">Activity today</span>
                <strong>{health.activityToday}</strong>
              </div>
              <div className="health-card">
                <span className="health-label">Portal logins today</span>
                <strong>{health.portalLoginsToday}</strong>
              </div>
              <div className="health-card">
                <span className="health-label">Staff logins OK / fail</span>
                <strong>{health.loginsToday?.success || 0} / {health.loginsToday?.failed || 0}</strong>
              </div>
              <div className="health-card">
                <span className="health-label">Pending applications</span>
                <strong>{health.counts?.applications ?? '—'}</strong>
              </div>
            </div>
          )}

          {health?.counts && (
            <div className="system-counts">
              {Object.entries(health.counts).map(([k, v]) => (
                <div key={k} className="count-chip">
                  <span>{k.replace(/_/g, ' ')}</span>
                  <strong>{v ?? '—'}</strong>
                </div>
              ))}
            </div>
          )}

          <div className="charts-grid">
            <DualBarChart title="Login attempts (14 days)" data={analytics?.loginByDay} />
            <BarChart
              title="User activity (14 days)"
              data={analytics?.activityByUser}
              valueKey="actions"
              labelKey="user_email"
              color="#1e3a5f"
            />
            <BarChart
              title="Activity by hour (last 24h)"
              data={(analytics?.activityByHour || []).map((h) => ({
                ...h,
                hourLabel: `${String(h.hour).padStart(2, '0')}:00`,
              }))}
              valueKey="actions"
              labelKey="hourLabel"
              color="#0e7490"
            />
            <BarChart
              title="Member portal logins"
              data={analytics?.portalActivity}
              valueKey="logins"
              labelKey="day"
              color="#047857"
            />
          </div>
        </>
      )}

      {tab === 'users' && (
        <div>
          <form className="transaction-form" onSubmit={createUser} style={{ marginBottom: '1.5rem' }}>
            <h4 className="transaction-form-title">Add staff user</h4>
            <div className="transaction-form-grid">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  className="form-input"
                  type="email"
                  required
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <input
                  className="form-input"
                  type="text"
                  required
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select
                  className="form-input"
                  value={userForm.role}
                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                >
                  <option value="admin">Admin</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="marketing">Marketing</option>
                  <option value="accountant">Accountant</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
            </div>
            <button type="submit" className="btn btn-primary">Create User</button>
          </form>

          <div className="table-responsive">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th className="table-header">ID</th>
                  <th className="table-header">Email</th>
                  <th className="table-header">Role</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="table-row">
                    <td className="table-cell">{u.id}</td>
                    <td className="table-cell">{u.email}</td>
                    <td className="table-cell">
                      <select
                        className="form-input"
                        value={u.role || 'viewer'}
                        onChange={(e) => updateUserRole(u.id, e.target.value)}
                      >
                        <option value="admin">admin</option>
                        <option value="supervisor">supervisor</option>
                        <option value="marketing">marketing</option>
                        <option value="accountant">accountant</option>
                        <option value="viewer">viewer</option>
                      </select>
                    </td>
                    <td className="table-cell">
                      <button className="btn btn-delete" type="button" onClick={() => deleteUser(u)}>🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'accounts' && (
        <div className="table-responsive">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th className="table-header">Login key</th>
                <th className="table-header">Email</th>
                <th className="table-header">Created</th>
                <th className="table-header">Last login</th>
                <th className="table-header">Actions</th>
              </tr>
            </thead>
            <tbody>
              {memberAccounts.map((a) => (
                <tr key={a.id} className="table-row">
                  <td className="table-cell">{a.employment_number}</td>
                  <td className="table-cell">{a.email}</td>
                  <td className="table-cell">{a.created_at ? new Date(a.created_at).toLocaleString() : '—'}</td>
                  <td className="table-cell">{a.last_login ? new Date(a.last_login).toLocaleString() : 'Never'}</td>
                  <td className="table-cell">
                    <button className="btn btn-delete" type="button" onClick={() => deleteMemberAccount(a)}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'logs' && (
        <div>
          <p style={{ marginBottom: '0.75rem', color: '#64748b', fontSize: '0.9rem' }}>
            Full activity stream. For a clearer who / what / when view, open <strong>Change History</strong> in the sidebar.
          </p>
          <div className="table-responsive">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th className="table-header">When</th>
                <th className="table-header">Who</th>
                <th className="table-header">Role</th>
                <th className="table-header">What</th>
                <th className="table-header">Status</th>
                <th className="table-header">IP</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="table-row">
                  <td className="table-cell">{l.created_at ? new Date(l.created_at).toLocaleString() : '—'}</td>
                  <td className="table-cell">{l.user_email || '—'}</td>
                  <td className="table-cell">{l.user_role || '—'}</td>
                  <td className="table-cell">{l.summary || l.action}</td>
                  <td className="table-cell">{l.status_code}</td>
                  <td className="table-cell">{l.ip_address || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {tab === 'logins' && (
        <div className="table-responsive">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th className="table-header">Time</th>
                <th className="table-header">Email</th>
                <th className="table-header">Type</th>
                <th className="table-header">Result</th>
                <th className="table-header">Reason</th>
                <th className="table-header">IP</th>
              </tr>
            </thead>
            <tbody>
              {loginAttempts.map((l) => (
                <tr key={l.id} className="table-row">
                  <td className="table-cell">{l.created_at ? new Date(l.created_at).toLocaleString() : '—'}</td>
                  <td className="table-cell">{l.email || '—'}</td>
                  <td className="table-cell">{l.login_type}</td>
                  <td className="table-cell">
                    <span className={`status-badge status-${Number(l.success) ? 'approved' : 'pending'}`}>
                      {Number(l.success) ? 'Success' : 'Failed'}
                    </span>
                  </td>
                  <td className="table-cell">{l.reason || '—'}</td>
                  <td className="table-cell">{l.ip_address || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SystemAdminPanel;
